/**
 * Typed factory functions for JSON-LD structured data schemas.
 *
 * Uses schema-dts types for full Schema.org type safety.
 * Pass the returned object directly to <JsonLd data={...} />.
 *
 * @example
 * <JsonLd data={createOrganizationSchema()} />
 * <JsonLd data={createFAQPageSchema(items)} />
 */

import type {
  WithContext,
  Organization,
  FAQPage,
  Article,
  MedicalWebPage,
  MedicalClinic,
  WebSite,
  Product,
  Review,
} from "schema-dts"
import { SITE_URL, absoluteUrl } from "@/lib/site-url"
import { getCountryConfigStatic, publicCountries } from "@/i18n/config"

// ---------------------------------------------------------------------------
// Organization — used on homepage
// ---------------------------------------------------------------------------

/**
 * Stable node identifiers. Without an `@id`, every page emits an anonymous
 * Organization and search engines have to guess that the homepage's company
 * and /about's company are the same entity. A constant `@id` says so
 * explicitly, and lets other nodes (WebSite, Article publisher, …) reference
 * the one entity instead of restating it.
 *
 * The corollary: everything under this `@id` must be identical on every page
 * that emits it. Contradicting facts under one identifier is worse than no
 * identifier at all — which is why `createOrganizationSchema` no longer varies
 * by country.
 */
export const ORGANIZATION_ID = `${SITE_URL}/#organization`

export function createOrganizationSchema(): WithContext<Organization> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: "Ahead Health",
    legalName: "Ahead Health AG",
    alternateName: ["Ahead", "aheadhealth.com"],
    url: SITE_URL,
    logo: absoluteUrl("/icons/ahead-logo.svg"),
    // Swiss commercial-register UID, as published on /imprint. A registry
    // identifier is the strongest disambiguation signal available — it ties
    // the entity to a public record instead of leaving engines to resolve
    // "Ahead Health" by name alone (currently resolving to our LinkedIn page).
    identifier: {
      "@type": "PropertyValue",
      propertyID: "UID",
      value: "CHE-213.692.684",
    },
    founder: [
      { "@type": "Person", name: "Nick Lenten" },
      { "@type": "Person", name: "Sven Jaeschke" },
    ],
    contactPoint: {
      "@type": "ContactPoint",
      // The Swiss number only. The DE/AT entries in `i18n/config.ts` are
      // placeholders (`+49 30 00000000`), and this schema was publishing them
      // as fact on every /de/de page.
      telephone: getCountryConfigStatic("ch")!.phone,
      contactType: "customer service",
      email: "get@aheadhealth.com",
      availableLanguage: ["English", "German", "French"],
    },
    sameAs: [
      "https://www.linkedin.com/company/ahead-health/",
      "https://www.instagram.com/ahead.health",
      "https://www.facebook.com/aheadhealth",
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Uraniastrasse 31",
      addressLocality: "Zürich",
      postalCode: "8001",
      addressCountry: "CH",
    },
    areaServed: publicCountries.map((c) => ({
      "@type": "Country",
      name: c.name,
    })),
  } as WithContext<Organization>
}

// ---------------------------------------------------------------------------
// WebSite — used on homepage
// ---------------------------------------------------------------------------

export function createWebSiteSchema(
  country = "ch",
  locale = "en"
): WithContext<WebSite> {
  // Google's Sitelinks Searchbox requires target to be an EntryPoint object
  // with urlTemplate, plus a sibling "query-input" property. schema-dts doesn't
  // type the hyphenated "query-input" key, so we cast at the return boundary.
  //
  // NOTE on Google specifically: Google announced in late 2024 that they're
  // discontinuing the sitelinks search box and the box no longer renders in
  // Search results. The markup still tells Bing/DuckDuckGo + reads as general
  // structured-data hygiene, so we keep emitting it pointed at the localized
  // /search route (which actually works thanks to `lib/search/`).
  const localeBase = `${SITE_URL}/${country}/${locale}`
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    // Scoped per locale because `url` and the search target are per locale.
    // The shared publisher reference is what ties every locale's site back to
    // the one Organization entity.
    "@id": `${localeBase}/#website`,
    name: "Ahead Health",
    url: localeBase,
    publisher: { "@id": ORGANIZATION_ID },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${localeBase}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  } as WithContext<WebSite>
}

// ---------------------------------------------------------------------------
// FAQPage — used on /faq, /faq/[slug], /faq/category/[slug]
// ---------------------------------------------------------------------------

export interface FaqQuestion {
  question: string
  answer: string
  /** FAQ slug — used for @id and acceptedAnswer.url */
  slug?: string
  /** ISO date string — used for dateModified on each Question */
  updatedAt?: string
}

export function createFAQPageSchema(
  questions: FaqQuestion[],
  options?: { name?: string; localeBase?: string }
): WithContext<FAQPage> {
  const base = options?.localeBase ?? SITE_URL
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(options?.name ? { name: options.name } : {}),
    mainEntity: questions.map((q) => ({
      "@type": "Question" as const,
      name: q.question,
      ...(q.slug ? { "@id": `${base}/faq/${q.slug}#question` } : {}),
      acceptedAnswer: {
        "@type": "Answer" as const,
        text: q.answer,
        ...(q.slug ? { url: `${base}/faq/${q.slug}` } : {}),
      },
      ...(q.updatedAt ? { dateModified: q.updatedAt } : {}),
    })),
  }
}

// ---------------------------------------------------------------------------
// Article — used on /insights/[slug]
// ---------------------------------------------------------------------------

export interface ArticleSchemaInput {
  slug: string
  title: string
  excerpt: string
  publishedDate: string
  author?: string
  authorSlug?: string
  authorPhoto?: string
  heroImage?: string
}

export function createArticleSchema(
  post: ArticleSchemaInput,
  localeBase?: string
): WithContext<Article> {
  const base = localeBase ?? SITE_URL
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    ...(post.heroImage ? { image: absoluteUrl(post.heroImage) } : {}),
    datePublished: post.publishedDate,
    author: {
      "@type": "Person",
      name: post.author ?? "Ahead Health",
      ...(post.authorPhoto ? { image: absoluteUrl(post.authorPhoto) } : {}),
      ...(post.authorSlug ? { url: `${base}/authors/${post.authorSlug}` } : {}),
    },
    publisher: {
      "@type": "Organization",
      name: "Ahead Health",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/icons/favicon.png`,
      },
    },
    mainEntityOfPage: `${base}/insights/${post.slug}`,
  }
}

// ---------------------------------------------------------------------------
// MedicalWebPage — used on /tests/[slug]
// ---------------------------------------------------------------------------

export interface MedicalWebPageSchemaInput {
  slug: string
  name: string
  description: string
  image?: string
}

export function createMedicalWebPageSchema(
  test: MedicalWebPageSchemaInput,
  localeBase?: string
): WithContext<MedicalWebPage> {
  const base = localeBase ?? SITE_URL
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: test.name,
    description: test.description,
    ...(test.image ? { image: absoluteUrl(test.image) } : {}),
    url: `${base}/tests/${test.slug}`,
    mainEntity: {
      "@type": "MedicalTest",
      name: test.name,
      description: test.description,
      usesDevice: {
        "@type": "MedicalDevice",
        name: test.name,
      },
    },
    provider: {
      "@type": "Organization",
      name: "Ahead Health",
      url: SITE_URL,
    },
  }
}

// ---------------------------------------------------------------------------
// MedicalWebPage (biomarker glossary) — used on /biomarkers/[slug]
// ---------------------------------------------------------------------------

export interface BiomarkerMedicalWebPageInput {
  slug: string
  name: string
  /** SERP summary — the same string used for the meta description. */
  description: string
  /** "Also known as" synonyms, emitted as the entity's alternateName. */
  aliases?: string[]
  /** LOINC code from the clinical range record, when present. */
  loinc?: string | null
  /** CMS `updatedAt` — emitted as dateModified (NOT lastReviewed: we have no
   *  reviewer/review-date field, and asserting a medical-review event we can't
   *  substantiate would be a false E-E-A-T signal). */
  updatedAt?: string | null
  /** URL locale (en/de/fr/nl) for inLanguage. */
  locale: string
}

/**
 * MedicalWebPage for a biomarker glossary page. Declares the page as medical
 * content and links `about` + `mainEntity` → the biomarker as a `MedicalEntity`
 * via a shared `@id`, so the entity is described once and referenced from the
 * page node (a proper Schema.org graph, not a duplicated blob).
 *
 * Note: MedicalWebPage produces NO Google rich result (absent from the search
 * gallery) — its value is entity understanding for Google's index and citation
 * by AI answer engines (GEO). `localeBase` is the locale-scoped origin, e.g.
 * `${SITE_URL}/ch/fr`.
 *
 * Cast through the return type because schema-dts' medical subtypes are narrow
 * (no `@id` on nested medical entities, no bare `{"@id"}` ref for mainEntity)
 * and our payload is intentionally the richer Schema.org graph shape — same
 * reason the biomarker page casts its MedicalEntity node.
 */
export function createBiomarkerMedicalWebPageSchema(
  input: BiomarkerMedicalWebPageInput,
  localeBase: string
): WithContext<MedicalWebPage> {
  const canonical = `${localeBase}/biomarkers/${input.slug}`
  const entityId = `${canonical}#biomarker`
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: input.name,
    description: input.description,
    inLanguage: input.locale,
    ...(input.updatedAt ? { dateModified: input.updatedAt } : {}),
    medicalAudience: {
      "@type": "MedicalAudience",
      audienceType: "Patient",
    },
    about: {
      "@type": "MedicalEntity",
      "@id": entityId,
      name: input.name,
      ...(input.aliases?.length ? { alternateName: input.aliases } : {}),
      description: input.description,
      url: canonical,
      ...(input.loinc
        ? {
            code: {
              "@type": "MedicalCode",
              codeValue: input.loinc,
              codingSystem: "LOINC",
            },
          }
        : {}),
    },
    mainEntity: { "@id": entityId },
    isPartOf: {
      "@type": "WebSite",
      url: SITE_URL,
      name: "Ahead Health",
    },
  } as unknown as WithContext<MedicalWebPage>
}

// ---------------------------------------------------------------------------
// MedicalClinic — used on /locations/[slug]
// ---------------------------------------------------------------------------

export interface MedicalClinicSchemaInput {
  name: string
  address: string
  city: string
  /** Lowercase country code (e.g. "ch", "nl") — emitted as ISO addressCountry. */
  country: string
  type: "mri" | "blood"
  lat: number
  lng: number
}

export interface MedicalClinicSchemaOptions {
  /**
   * City names the clinic serves, beyond its own postal city. Emitted as
   * `areaServed: City[]` so SEO crawlers see explicit service-area signal
   * for city-targeted landing pages (e.g. `/full-body-mri-zurich`).
   */
  areaServed?: string[]
}

export function createMedicalClinicSchema(
  clinic: MedicalClinicSchemaInput,
  options: MedicalClinicSchemaOptions = {}
): WithContext<MedicalClinic> {
  const schema: WithContext<MedicalClinic> = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: clinic.name,
    address: {
      "@type": "PostalAddress",
      streetAddress: clinic.address.split(",")[0]?.trim(),
      addressLocality: clinic.city,
      addressCountry: clinic.country.toUpperCase(),
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: clinic.lat,
      longitude: clinic.lng,
    },
    medicalSpecialty: clinic.type === "mri" ? "Radiography" : "Pathology",
    parentOrganization: {
      "@type": "Organization",
      name: "Ahead Health",
      url: SITE_URL,
    },
  }

  if (options.areaServed?.length) {
    schema.areaServed = options.areaServed.map((name) => ({
      "@type": "City",
      name,
    }))
  }

  return schema
}

// ---------------------------------------------------------------------------
// Product + Offers — used on /pricing
// ---------------------------------------------------------------------------

export interface PricingOfferInput {
  name: string
  /** Formatted price string like "1'990" — apostrophes are stripped for schema */
  price: string
  description?: string
  image?: string
}

export function createPricingProductSchema(
  offers: PricingOfferInput[],
  country: string,
  locale: string,
  // Defaults describe the /pricing product. A product page (e.g. the full-body
  // MRI) passes its own name/description and localized `path` so the Product +
  // Offers ride the page that actually ranks for the money query, keyed on the
  // same live prices — the JSON-LD price can't drift from the cards.
  opts?: { name?: string; description?: string; path?: string }
): WithContext<Product> {
  const currency = getCountryConfigStatic(country)!.currency
  const url = `${SITE_URL}/${country}/${locale}/${opts?.path ?? "pricing"}`
  const images = offers
    .filter((o): o is PricingOfferInput & { image: string } => Boolean(o.image))
    .map((o) => absoluteUrl(o.image))

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: opts?.name ?? "Ahead Health Preventive Check-up",
    description:
      opts?.description ??
      "Comprehensive preventive health check-up packages including full-body MRI, blood tests, and AI-assisted analysis.",
    brand: { "@type": "Brand", name: "Ahead Health" },
    url,
    ...(images.length > 0 ? { image: images } : {}),
    offers: offers.map((o) => ({
      "@type": "Offer" as const,
      name: o.name,
      price: Number(o.price.replace(/'/g, "")),
      priceCurrency: currency,
      url,
      availability: "https://schema.org/InStock",
      ...(o.description ? { description: o.description } : {}),
    })),
  }
}

// ---------------------------------------------------------------------------
// Reviews + AggregateRating — used on /reviews
// ---------------------------------------------------------------------------

export interface ReviewSchemaInput {
  author: string
  reviewBody: string
  rating: number
  source: "google" | "trustpilot"
  date?: string
  url?: string
}

/** Wrap the Organization with an AggregateRating + per-review list, so a
 *  single JSON-LD blob on /reviews carries everything LLMs and search
 *  engines look for: the entity, its overall rating, and the individual
 *  testimonials. */
export function createOrganizationReviewsSchema(
  reviews: ReviewSchemaInput[]
): WithContext<Organization> {
  // Shares ORGANIZATION_ID with the plain node, so the rating and reviews
  // attach to the same entity rather than describing a second company.
  const org = createOrganizationSchema()
  const ratingValue =
    reviews.length === 0
      ? 5
      : reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length

  const reviewLd: Review[] = reviews.map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.author },
    reviewBody: r.reviewBody,
    reviewRating: {
      "@type": "Rating",
      ratingValue: r.rating,
      bestRating: 5,
      worstRating: 1,
    },
    publisher: {
      "@type": "Organization",
      name: r.source === "google" ? "Google" : "Trustpilot",
    },
    ...(r.date ? { datePublished: r.date } : {}),
    ...(r.url ? { url: r.url } : {}),
    itemReviewed: { "@type": "Organization", name: "Ahead Health" },
  }))

  // `WithContext<Organization>` resolves to a discriminated union of every
  // Organization sub-type in schema-dts, which TS refuses to spread (a
  // union has no single statically-known shape). Round-trip via `unknown`
  // to widen to a plain record we can spread, then re-brand the final
  // object back to `WithContext<Organization>` on return.
  return {
    ...(org as unknown as Record<string, unknown>),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: Number(ratingValue.toFixed(1)),
      reviewCount: reviews.length,
      bestRating: 5,
      worstRating: 1,
    },
    review: reviewLd,
  } as unknown as WithContext<Organization>
}
